import React from 'react';
import type { FunnelIndicators } from '../api';
import { formatHours } from './format';

interface IndicadoresFunilProps {
  data?: FunnelIndicators;
  loading?: boolean;
}

/**
 * Cartão de indicadores do funil: quantos estão em cada etapa, quanto
 * avança e quanto tempo demora.
 *
 * Etapa sem movimentação aparece zerada em vez de sumir — uma coluna
 * ausente esconderia justamente o gargalo onde ninguém entra.
 */
export function IndicadoresFunil({ data, loading }: IndicadoresFunilProps) {
  if (loading) {
    return <div className="h-32 animate-pulse rounded-lg bg-gray-100" />;
  }
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Totalizador label="Candidaturas" value={data.totals.applications} />
        <Totalizador label="Aprovados" value={data.totals.approved} tone="text-green-700" />
        <Totalizador label="Reprovados" value={data.totals.rejected} tone="text-red-700" />
        <Totalizador
          label="Banco de talentos"
          value={data.totals.talentPool}
          tone="text-purple-700"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Etapa</th>
              <th className="px-3 py-2 text-right">Na etapa</th>
              <th className="px-3 py-2 text-right">Avançaram</th>
              <th className="px-3 py-2 text-right">Reprovados</th>
              <th className="px-3 py-2 text-right">Taxa de aprovação</th>
              <th className="px-3 py-2 text-right">Tempo médio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {data.stages.map((stage) => (
              <tr key={stage.key}>
                <td className="px-3 py-2 font-medium text-gray-900">{stage.label}</td>
                <td className="px-3 py-2 text-right">{stage.current}</td>
                <td className="px-3 py-2 text-right">{stage.advanced}</td>
                <td className="px-3 py-2 text-right">{stage.rejected}</td>
                <td className="px-3 py-2 text-right">
                  {stage.approvalRate === null ? '—' : `${stage.approvalRate}%`}
                </td>
                <td className="px-3 py-2 text-right">{formatHours(stage.averageHours)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Totalizador({
  label,
  value,
  tone = 'text-gray-900',
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tone}`}>{value}</p>
    </div>
  );
}
